import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileTextIcon, PlusIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type React from "react";
import type { User } from "@/features/auth/services/firebase";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import { cn } from "@/lib/utils";
import { Command, type CommandItem } from "@/components/ui/command";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  safeLocalStorageGet,
  safeLocalStorageSet,
} from "@/utils/safe-local-storage";
import { useCommandAuditSearch } from "@/hooks/useCommandAuditSearch";
import { AppUpdateBanner } from "./AppUpdateBanner";
import { StagingBanner } from "./StagingBanner";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { getNavGroups } from "./nav-model";

const SIDEBAR_COLLAPSED_STORAGE_KEY = "vardic_sidebar_collapsed";

/**
 * AppShell — the Calm Authority frame: a fixed deep-ink sidebar (a Sheet drawer
 * on mobile) beside a column of the topbar over the scrolling page content, with
 * a globally mounted ⌘K command palette. Wraps only authed + onboarded routes;
 * auth screens render outside it.
 */
export function AppShell({
  user,
  profile,
  pathname,
  onSignOut,
  children,
}: {
  user: User;
  profile: UserProfile | null;
  pathname: string;
  onSignOut: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  // Desktop-only icon-rail collapse; persisted so the preference survives
  // navigation/reload (the mobile Sheet drawer never reads this — it has no
  // use for a collapse toggle of its own, see Sidebar's doc comment).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => safeLocalStorageGet(SIDEBAR_COLLAPSED_STORAGE_KEY) === "1",
  );
  const toggleSidebarCollapsed = (): void => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    safeLocalStorageSet(SIDEBAR_COLLAPSED_STORAGE_KEY, next ? "1" : "0");
  };
  const { reset: resetAuditSearch, ...auditSearch } = useCommandAuditSearch();

  // ⌘K / Ctrl-K toggles the command palette from anywhere in the shell.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // The command palette's CommandBody remounts fresh on every open (its own
  // query state resets), but AppShell owns auditSearch's state across opens
  // -- reset it on close too, so reopening never briefly shows a stale
  // audit-search result from the previous session.
  useEffect(() => {
    if (!commandOpen) {
      resetAuditSearch();
    }
  }, [commandOpen, resetAuditSearch]);

  const commandItems: CommandItem[] = useMemo(() => {
    const items: CommandItem[] = [
      {
        id: "action-new-audit",
        label: t("shell.newAudit"),
        group: t("shell.groupWork"),
        icon: PlusIcon,
        onSelect: () => {
          void navigate("/");
        },
      },
    ];
    for (const group of getNavGroups()) {
      for (const item of group.items) {
        // The "/" audit route is already the synthetic "new audit" action above;
        // skip it here so the palette doesn't list the same destination twice.
        if (item.to === "/") {
          continue;
        }
        items.push({
          id: `nav-${item.to}`,
          label: t(item.labelKey),
          group: t(group.labelKey),
          icon: item.icon,
          onSelect: () => {
            void navigate(item.to);
          },
        });
      }
    }
    return items;
  }, [t, navigate]);

  const auditCommandItems: CommandItem[] = useMemo(
    () =>
      auditSearch.results.map((audit) => ({
        id: `audit-${audit.id}`,
        label: audit.displayName ?? audit.documentName,
        group: t("shell.groupAudits"),
        icon: FileTextIcon,
        onSelect: () => {
          // Matches HistoryTable's own click rule: only a COMPLETED audit has
          // a results view to jump to; anything else lands on History, where
          // its live status is visible.
          void navigate(
            audit.status === "COMPLETED" ? `/results/${audit.id}` : "/history",
          );
        },
      })),
    [auditSearch.results, t, navigate],
  );

  return (
    <div className="flex h-[100dvh] overflow-hidden" data-test="app-shell">
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-[var(--primary)] focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-[var(--primary-foreground)]"
        data-test="skip-to-content"
        href="#vardic-scroll"
      >
        {t("shell.skipToContent")}
      </a>
      <aside
        className={cn(
          "hidden shrink-0 overflow-hidden transition-[width] duration-[180ms] ease-[var(--ease-out)] md:block",
          sidebarCollapsed
            ? "w-[var(--sidebar-w-collapsed)]"
            : "w-[var(--sidebar-w)]",
        )}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onSignOut={onSignOut}
          onToggleCollapsed={toggleSidebarCollapsed}
          pathname={pathname}
          profile={profile}
          user={user}
        />
      </aside>

      <Sheet onOpenChange={setMobileNavOpen} open={mobileNavOpen}>
        <SheetContent
          className="p-0"
          data-test="mobile-nav"
          side="left"
          width={256}
        >
          {/* eslint-disable-next-line i18next/no-literal-string -- brand wordmark, not translatable */}
          <SheetTitle className="sr-only">Vardic</SheetTitle>
          <Sidebar
            onNavigate={() => setMobileNavOpen(false)}
            onSignOut={onSignOut}
            pathname={pathname}
            profile={profile}
            user={user}
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          onOpenCommand={() => setCommandOpen(true)}
          onOpenMobileNav={() => setMobileNavOpen(true)}
          pathname={pathname}
          profile={profile}
        />
        <AppUpdateBanner />
        <StagingBanner />
        <main
          className="flex-1 overflow-y-auto bg-[var(--background)] focus:outline-none"
          data-test="app-main"
          id="vardic-scroll"
          tabIndex={-1}
        >
          {children}
        </main>
      </div>

      <Command
        emptyText={t("shell.commandEmpty")}
        items={commandItems}
        label={t("shell.commandLabel")}
        onOpenChange={setCommandOpen}
        onQueryChange={auditSearch.setQuery}
        open={commandOpen}
        placeholder={t("shell.search")}
        remoteItems={auditCommandItems}
        remoteLoading={auditSearch.loading}
        remoteLoadingText={t("shell.commandSearching")}
      />
    </div>
  );
}
